import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import ViewerCount from './ViewerCount';

const navLinks = [
  { to: '/',        label: 'Home' },
  { to: '/settings', label: 'Settings' },
  { to: '/faq',     label: 'Faqs' },
  { to: '/live',    label: 'Live' },
  { to: '/rewards', label: 'Rewards' },
];

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.pointerEvents = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    };
  }, [open]);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <>
      <style>{`
        @keyframes handBounce {
          0%   { transform: translateX(0px); }
          35%  { transform: translateX(-12px); }
          65%  { transform: translateX(-5px); }
          85%  { transform: translateX(-10px); }
          100% { transform: translateX(0px); }
        }
        @keyframes handSlideIn {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0px);  opacity: 1; }
        }

        @keyframes livePulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }

        .nav-link-hover::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0%;
          height: 2px;
          background-color: #ff2a2a;
          border-radius: 9999px;
          transition: width 0.3s ease;
        }
        .nav-link-hover:hover::after { width: 100%; }

        .nav-link-active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: #ff2a2a;
          border-radius: 9999px;
        }
        .nav-link-active:hover::after { width: 100%; transition: none; }

        /* Live dot on the Live nav item */
        .live-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22c55e;
          margin-left: 5px;
          vertical-align: middle;
          animation: livePulse 1.6s ease-in-out infinite;
        }
      `}</style>

      <nav
        className="w-full bg-[#000000] border-b border-[#000000] h-20 flex items-center justify-center top-0 left-0 z-[200]"
        style={{ pointerEvents: 'auto', position: 'relative' }}
      >
        <div className="w-full max-w-7xl flex items-center justify-between px-6">

          <Link to="/" className="flex items-center space-x-3">
            <img src={logo} alt="Logo" className="w-8 h-8 rounded-full object-cover border-4 border-white" />
            <span className="hidden md:inline font-extrabold text-lg tracking-widest text-white">
              150k_btw
            </span>
          </Link>

          <div className="absolute left-1/2 -translate-x-1/2 md:hidden">
            <ViewerCount />
          </div>

          {/* Desktop links */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((l) => {
              const active = isActive(l.to);
              const isLiveLink = l.to === '/live';
              const isRewards  = l.label === 'Rewards';
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`font-medium transition-colors relative pb-1 ${
                    active
                      ? 'text-[#ff2a2a] nav-link-active'
                      : isLiveLink || isRewards
                        ? 'text-[#ff2a2a] nav-link-hover'
                        : 'text-gray-200 hover:text-[#ff2a2a] nav-link-hover'
                  }`}
                >
                  {l.label}
                  {isLiveLink}
                </Link>
              );
            })}
            <ViewerCount />
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-[6px] focus:outline-none"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <span className={`block h-[2px] bg-white rounded-full transition-all duration-300 origin-center ${open ? 'w-6 rotate-45 translate-y-[8px]' : 'w-6'}`} />
            <span className={`block h-[2px] bg-white rounded-full transition-all duration-300 ${open ? 'w-0 opacity-0' : 'w-4'}`} />
            <span className={`block h-[2px] bg-white rounded-full transition-all duration-300 origin-center ${open ? 'w-6 -rotate-45 -translate-y-[8px]' : 'w-6'}`} />
          </button>
        </div>
      </nav>

      {/* Mobile full-screen menu */}
      <div
        className={`fixed inset-0 z-[150] bg-black flex flex-col justify-center items-start px-10 transition-all duration-500 md:hidden ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ pointerEvents: open ? 'auto' : 'none' }}
      >
        <div className={`w-12 h-[3px] bg-[#ff2a2a] mb-10 transition-all duration-500 delay-100 ${open ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} />

        <nav className="flex flex-col gap-7 w-full pr-14">
          {navLinks.map((link, i) => {
            const active      = isActive(link.to);
            const isLiveLink  = link.to === '/live';
            return (
              <div key={link.to} className="relative flex items-center">
                <Link
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`text-5xl font-extrabold tracking-tight leading-none transition-all duration-500 ${
                    active || link.label === 'Rewards' || isLiveLink
                      ? 'text-[#ff2a2a]'
                      : 'text-white hover:text-[#ff2a2a]'
                  } ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: open ? `${150 + i * 80}ms` : `${(navLinks.length - i) * 40}ms` }}
                >
                  {link.label}
                  {isLiveLink}
                </Link>
                {active && open && (
                  <span
                    className="absolute right-0 text-3xl select-none"
                    style={{
                      animation: `
                        handSlideIn 0.4s ease-out ${150 + i * 80 + 180}ms both,
                        handBounce 1.3s ease-in-out ${150 + i * 80 + 580}ms infinite
                      `,
                    }}
                  >
                    👈
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        <div
          className={`mt-14 transition-all duration-500 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: open ? '600ms' : '0ms' }}
        >
          <ViewerCount />
        </div>
      </div>
    </>
  );
};

export default Navbar;